// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ProjectModel } from '@project/model/project.model';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO, SelectionMetaElementUsageIdentity, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ArrayUtils } from '@shared/util/array-utils';
import { DoubleSide, Material, Mesh, Vector3 } from 'three';
import { ConnectionAreaType, ConnectionCreator, ConnectionShapeType } from '@article/components/connections/connection-creation/connection-creator';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ConnectionDefinition } from '@article/components/connections/connection-creation/connection-configuration';
import { ConnectionConverter } from '@article/components/connections/connection-creation/connection-converter';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { CloneUtils } from '@shared/util/clone-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { AdminService } from '../services/admin.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

export interface ConnectionAreaProperties {
  featureProperties: PropertyVO[];
  requirementProperties: PropertyVO[];
  plausiProperties: PropertyVO[];
}

interface ConnectionPosition {
  position: Vector3;
  rotationDegree: Vector3;
  widthOrRadius: number;
  length: number;
}

export class BaseConnectionOperation {

  private _metaElement: MetaElementVO;
  private _mesh: Mesh;

  private _connectionColor: string;
  private _connectionImage: HTMLImageElement;
  private _connectionMultiplicity: number;
  private _connectionOpacity: number;

  constructor(
    protected adminService: AdminService,
    private projectModel: ProjectModel,
    protected selectionModel: SelectionModel,
    protected selectionModelService: SelectionModelService,
    private sceneManager: SceneManager,
    private projectArticleManager: ProjectArticleManager,
    private connectionCreator: ConnectionCreator,
    private connectionConverter: ConnectionConverter,
    private _connection: Connection,
    protected _articleContainer: ArticleContainer,
    protected _article: ArticleVO,
    private _applicationRefreshService: ApplicationRefreshService,
    private _connections?: Connection[]
  ) {
    this._metaElement = CloneUtils.cloneMetaElementVO(_connection.metaElementVO);
    this._mesh = _connection.mesh.clone() as Mesh;
    this._connectionColor = _connection.getColorAsHexString();
    this._connectionImage = ConnectionInfoUtils.getConnectionImage(_connection);
    this._connectionMultiplicity = this.getMultiplicity(_connection);
    this._connectionOpacity = _connection.getDefaultOpacity();
  }

  private getMultiplicity(connection: Connection): number {
    if (!connection) {
      return;
    }
    const meta: MetaElementVO = connection.metaElementVO;
    if (meta) {
      if (meta instanceof ConnectionAreaVO) {
        return meta.connectionMultiplicity;
      }
    }
    return null;
  }

  public attachConnection(): void {
    const connection: Connection = this.connectionCreator.createConnection(
      {
        connectionForm: this.connectionCreator.getConnectionShapeType(this._connection),
        connectionType: this.connectionCreator.getConnectionTypeByMetaElement(this._connection.metaElementVO),
        connectionInfo: this.connectionCreator.getConnectionInfoByMetaElement(this._connection.metaElementVO),
        connectionName: this._connection.connectionName,
        connectionMultiplicity:
          this._connection.metaElementVO instanceof ConnectionAreaVO ? this._connection.metaElementVO.connectionMultiplicity : null,
        connectionDoubleside: this.connectionCreator.isBothSidesConnection(this._connection.metaElementVO),
        connectionLength: this._connection.meshLength,
        connectionWidth: this._connection.meshWidth,
        connectionRadius: this._connection.meshRadius,
        bitmapData: this._connectionImage,
        connectionColor: this._connectionColor,
        position: null,
        rotation: null,
        metaElement: this._metaElement,
      },
      this._articleContainer,
      this._article
    );

    const length: number = !this._connection.meshIsCircle ? this._connection.meshLength : null;
    const meshWidthOrRadius: number = this._connection.meshIsCircle ? this._connection.meshRadius : this._connection.meshWidth;

    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      con.updateMesh(
        this._mesh.position,
        this.connectionCreator.applyAdditionalConnectionRotation(con, this._connection.meshRotationDegree),
        meshWidthOrRadius,
        length
      );
    });

    this.notifyProjectChange();

    const connectionSelectionIdentity: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
      this._articleContainer.articleGUID,
      connection
    );
    this.selectionModelService.selectObject(connectionSelectionIdentity, false, false);
    this.selectionModelService.removeAllSelections();
  }

  public detachConnection(disposeConnection: boolean = true): Connection[] {
    const connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    const detachedConnections: Connection[] = [];
    this.adminService.togglePickConnectorPositionMode(false);
    connections.forEach((con: Connection) => {
      let conIndex: number = 0;
      this._article.metaElements.forEach((metaElementVO: MetaElementVO) => {
        if (metaElementVO.name === con.connectionName) {
          conIndex = this._article.metaElements.indexOf(metaElementVO);
        }
      });
      this._article.metaElements.splice(conIndex, 1);
      let detachedConnection: Connection;
      if (disposeConnection) {
        detachedConnection = this._articleContainer.disposeConnectionByName(con.connectionName);
      } else {
        detachedConnection = this._articleContainer.removeConnectionByName(con.connectionName);
        detachedConnection.mesh?.parent?.remove(detachedConnection.mesh);
      }
      detachedConnections.push(detachedConnection);
    });

    this.notifyProjectChange();

    const selectionId: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
      this._articleContainer.articleGUID,
      connection
    );
    this.projectArticleManager.articleSelectionChanged();
    this.selectionModel.notifySingleSelectionChanged({selectionId, isSelected: false, selectionType: "selectionMetaElement"});
    return detachedConnections;
  }

  public moveConnectionToPosition(connectionPosition: ConnectionPosition): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      con.updateMesh(
        connectionPosition.position,
        this.connectionCreator.applyAdditionalConnectionRotation(con, connectionPosition.rotationDegree),
        connectionPosition.widthOrRadius,
        connectionPosition.length
      );
    });

    this.notifyProjectChange();
  }

  public changeConnectionType(
    connectionDefinitionFrom: ConnectionDefinition,
    connectionDefinitionTo: ConnectionDefinition,
    connectionProperties?: ConnectionAreaProperties
  ): void {
    this.selectionModelService.removeAllSelections();

    const connection: Connection = this._articleContainer.findConnection(connectionDefinitionFrom.name);
    if (connectionDefinitionTo.type === ConnectionAreaType.TEXTURE_AREA && connectionDefinitionTo.subtype === TextureAreaVO.TYPE_IMAGE) {
      if (IsThreeJsType.meshPhongMaterial(connection.getDefaultMaterial())) {
        this.connectionCreator.applyImageToConnection(this._connectionImage, connection);
      }
    }

    const connections: Connection[] = this.connectionConverter.convertConnection(connection, connectionDefinitionTo);
    connections.forEach((con: Connection) => {
      if (con.metaElementVO instanceof ConnectionAreaVO) {
        if (connectionProperties) {
          con.metaElementVO.connectionFeatures = ConnectionInfoUtils.cloneProperties(connectionProperties.featureProperties);
          con.metaElementVO.connectionRequirements = ConnectionInfoUtils.cloneProperties(connectionProperties.requirementProperties);
          con.metaElementVO.connectionPlausiProperties = ConnectionInfoUtils.cloneProperties(connectionProperties.plausiProperties);
        }
        con.metaElementVO.connectionMultiplicity = this._connectionMultiplicity;
        con.setDefaultOpacity(this._connectionOpacity);
      }
    });

    if (connections && connections.length) {
      const connectionToSelect: Connection = connections.find((c) => c.metaElementVO && !c.metaElementVO.isInternal);
      if (connectionToSelect) {
        const selectionIdentity: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
          connectionToSelect.articleContainer.articleGUID,
          connectionToSelect
        );
        this.selectionModelService.selectObject(selectionIdentity);
      }
    }
    this.notifyProjectChange();
  }

  public changeConnectionSubType(connectionDefinition: ConnectionDefinition): void {
    const type: string = connectionDefinition.type;
    const subtype: string = connectionDefinition.subtype;
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    if (connection && connection.metaElementVO instanceof ConnectionAreaVO) {
      if (type === ConnectionAreaType.CONNECTION_AREA) {
        connection.metaElementVO.connectionSide = subtype;
        if (subtype === ConnectionAreaVO.SIDE_ACCEPTING) {
          this.moveConnectionPropertiesToSide(connection.metaElementVO.connectionFeatures, connection.metaElementVO.connectionRequirements);
        } else if (subtype === ConnectionAreaVO.SIDE_GIVING) {
          this.moveConnectionPropertiesToSide(connection.metaElementVO.connectionRequirements, connection.metaElementVO.connectionFeatures);
        }
      }
    }

    if (type === ConnectionAreaType.TEXTURE_AREA) {
      if (connection && connection.metaElementVO instanceof TextureAreaVO) {
        connection.metaElementVO.textureType = subtype;
        const updatedConnectionMaterial: Material = this.connectionConverter.getUpdatedConnectionMaterial(
          connection.getDefaultMaterial(),
          this.connectionCreator.getConnectionDefinitionByMetaElement(connection.metaElementVO, true),
          connection.getDefaultMaterial().side === DoubleSide
        );
        connection.setDefaultMaterial(updatedConnectionMaterial);
      }
      if (subtype === TextureAreaVO.TYPE_IMAGE) {
        if (IsThreeJsType.meshPhongMaterial(connection.getDefaultMaterial())) {
          this.connectionCreator.applyImageToConnection(this._connectionImage, connection);
        }
      }
    }
    this.sceneManager.renderScene();
    this.notifyProjectChange();
  }

  public changeConnectionShapeType(shapeType: ConnectionShapeType, length: number, width: number, radius: number): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const widthOrRadius = shapeType === ConnectionShapeType.CIRCLE ? radius : width;
    this.connectionConverter.convertConnectionForm(connection, shapeType);

    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      con.updateMesh(con.meshPosition, con.meshRotationDegree, widthOrRadius, length);
    });

    this.sceneManager.renderScene();
    this.notifyProjectChange();
  }

  public changeConnectionImage(imageURL: HTMLImageElement): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con) => {
      if (MetaElementUtils.isImageAllowed(con.metaElementVO)) {
        this.connectionCreator.applyImageToConnection(imageURL, con);
      }
    });
    this.sceneManager.renderScene();
    this.notifyProjectChange();
  }

  private moveConnectionPropertiesToSide(fromArray: PropertyVO[], toArray: PropertyVO[]): void {
    while (toArray && fromArray && fromArray.length > 0) {
      toArray.push(fromArray.pop());
    }
  }

  public undoConnectionColor(colorAsHexString: string): void {
    const colorAsNumber = parseInt(colorAsHexString.replace('#', '0x'), 16);
    this.changeConnectionColor(colorAsNumber);
    this.notifyProjectChange();
  }

  public redoConnectionColor(): void {
    const colorAsNumber = parseInt(this._connectionColor.replace('#', '0x'), 16);
    this.changeConnectionColor(colorAsNumber);
    this.notifyProjectChange();
  }

  private changeConnectionColor(colorAsNumber: number): void {
    if (ArrayUtils.isEmpty(this._connections)) {
      const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
      connection.setDefaultFillColor(colorAsNumber);
    } else {
      this._connections.forEach((con: Connection) => {
        const connection = this._articleContainer.findConnection(con.connectionName);
        connection.setDefaultFillColor(colorAsNumber);
      });
    }
    this.notifyProjectChange();
  }

  public changeConnectionMultiplicity(multiplicity: number): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      if (con.metaElementVO instanceof ConnectionAreaVO) {
        con.metaElementVO.connectionMultiplicity = multiplicity;
      }
    });

    this.notifyProjectChange();
  }

  public changeConnectionOpacity(opacity: number): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      con.setDefaultOpacity(opacity);
    });

    this.notifyProjectChange();
  }

  public renameConnection(newName: string): void {
    const connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((c: Connection) => {
      const currentMetaElementName: string = c.metaElementVO.name;
      let newMetaElementName: string = newName;
      if (MetaElementUtils.hasInternalConnectionPostfix(currentMetaElementName)) {
        newMetaElementName = MetaElementUtils.applyInternalConnectionPostfix(newMetaElementName);
      }
      c.articleContainer.changeConnectionKeyInConnectionsMap(currentMetaElementName, newMetaElementName);
      c.connectionName = newMetaElementName;
      c.metaElementVO.name = newMetaElementName;

      if (c.selected) {
        const selectedObjects = this.selectionModel.selectedObjects;
        selectedObjects.forEach((selectedObject) => {
          if (selectedObject instanceof SelectionMetaElementUsageIdentity && selectedObject.connectionUid === c.uid) {
            selectedObject.metaElementName = c.connectionName;
          }
        });
      }

      if (this._connection.connectionName === currentMetaElementName) {
        this._connection.connectionName = newMetaElementName;
        this._connection.metaElementVO.name = newMetaElementName;
      }
    });

    this.notifyProjectChange();
  }

  public changeConnectionOrderNumber(orderNumber: number): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      if (con.metaElementVO) {
        con.metaElementVO.orderNumber = orderNumber;
      }
    });

    this.notifyProjectChange();
  }

  public changeConnectionCorridor(corridor: number): void {
    const connection: Connection = this._articleContainer.findConnection(this._connection.connectionName);
    const connections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    connections.forEach((con: Connection) => {
      if (con.metaElementVO) {
        con.metaElementVO.corridor = corridor;
      }
    });

    this.notifyProjectChange();
  }

  protected notifyProjectChange(): void {
    this._applicationRefreshService.notifyProjectChange();
    this.projectModel.notifyCurrentProjectChanged();
  }
}
